import { saveRun, updateRun, deleteRun } from '../lib/grit-data.js';
import { localISO } from '../lib/phase.js';
import { confirmBox } from './dialog.js';
import { toast } from './toast.js';

let overlay = null;

const fmt = (sec) => `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}`;

// existing: a logged grit_runs row to edit or delete (plan v4: logged runs are editable).
export function openRunLog({ defaultType = 'easy', mode = 'treadmill', defaultDate = null, planContext = null, existing = null, onSaved }) {
  const paceText = (c) => (c ? (c.minutesOnly ? `${Math.round(c.totalSec / 60)} min` : mode === 'outside' ? `${c.pace} /mi` : `${c.pace} /mi · ${c.mph} mph`) : '--');
  close();
  const types = ['easy', 'runwalk', 'long', 'intervals', 'tempo', 'timetrial', 'lowimpact'];
  const TYPE_LABEL = { timetrial: 'mile test', lowimpact: 'bike / walk', runwalk: 'run/walk' };
  const exMin = existing?.actual_minutes != null ? Number(existing.actual_minutes) : null;
  const state = {
    type: existing?.type || planContext?.type || defaultType,
    date: existing?.date || defaultDate || localISO(),
    dist: existing ? (existing.actual_distance_mi != null ? String(existing.actual_distance_mi) : '') : (planContext?.plannedDistanceMi != null ? String(planContext.plannedDistanceMi) : ''),
    min: exMin != null ? String(Math.floor(exMin)) : (planContext?.plannedMinutes != null ? String(planContext.plannedMinutes) : ''),
    sec: exMin != null ? String(Math.round((exMin % 1) * 60)) : '',
    rpe: existing?.rpe ?? null, knee: existing?.knee_feel || 'good',
  };

  overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-[120] flex items-end justify-center bg-black/30';
  document.body.appendChild(overlay);

  // Low-impact cardio (bike, elliptical, incline walk) is logged by minutes only, and
  // any run can be logged by time alone (a 20-minute run/walk needs no invented miles).
  const minutesOnly = () => state.type === 'lowimpact';
  function calc() {
    const d = Number(state.dist), totalSec = Number(state.min || 0) * 60 + Number(state.sec || 0);
    if (minutesOnly() || (!d && totalSec)) return totalSec ? { totalSec, minutesOnly: true } : null;
    if (!d || !totalSec) return null;
    const paceSec = totalSec / d;
    return { paceSec, pace: fmt(paceSec), mph: Math.round((3600 / paceSec) * 10) / 10, totalSec, dist: d };
  }

  function paint() {
    const c = calc();
    overlay.innerHTML = `
      <div class="grit-sheet overflow-auto">
        <div class="grit-sheet-handle"></div>
        <div class="flex items-center justify-between mb-3">
          <div class="text-[20px] font-bold tracking-tight text-ink">${existing ? 'Edit run' : 'Log a run'}</div>
          ${existing ? '<button id="del-run" class="text-danger text-[15px] font-semibold min-h-[44px] px-1">Delete</button>' : ''}
        </div>

        <div class="flex gap-2 overflow-x-auto pb-1 mb-3" style="scrollbar-width:none">
          ${types.map((t) => `<button data-type="${t}" class=\"flex-none grit-pill ${state.type === t ? '!bg-ink !text-white' : ''}\">${TYPE_LABEL[t] || t}</button>`).join('')}
        </div>

        <label class="block mb-3"><span class="grit-eyebrow block mb-1">Date</span>
          <input data-f="date" type="date" value="${state.date}" class="grit-input"></label>

        <div class="grid grid-cols-2 gap-3">
          ${minutesOnly() ? '' : `<label class="block"><span class="grit-eyebrow block mb-1">Distance (mi, optional)</span>
            <input data-f="dist" type="number" inputmode="decimal" value="${state.dist}" class="grit-input text-center text-lg font-bold" placeholder="3.0"></label>`}
          <div><span class="grit-eyebrow block mb-1">Time</span>
            <div class="flex items-center gap-1">
              <input data-f="min" type="number" inputmode="numeric" value="${state.min}" class="grit-input text-center text-lg font-bold" placeholder="min">
              <span class="font-bold text-inksoft">:</span>
              <input data-f="sec" type="number" inputmode="numeric" value="${state.sec}" class="grit-input text-center text-lg font-bold" placeholder="sec">
            </div></div>
        </div>

        <div class="grit-card bg-pagebg p-3 mt-3 flex items-center justify-between">
          <span class="text-[13px] text-inksoft">${minutesOnly() ? 'Time' : 'Pace'}</span>
          <span class="text-[16px] font-bold text-ink">${paceText(c)}</span>
        </div>

        <span class="grit-eyebrow block mt-4 mb-1.5">How hard? (1 easy – 10 max)</span>
        <div class="grid grid-cols-5 gap-2 justify-items-center">
          ${Array.from({ length: 10 }, (_, i) => i + 1).map((n) => `<button data-rpe="${n}" class="w-11 h-11 rounded-full text-[15px] font-semibold active:scale-95 transition-transform ${state.rpe === n ? 'bg-ink text-white' : 'bg-fill text-ink'}">${n}</button>`).join('')}
        </div>

        <span class="grit-eyebrow block mt-4 mb-1.5">Knees</span>
        <div class="flex gap-2">
          ${[['good', 'Pain-free'], ['niggle', 'Niggle'], ['pain', 'Sharp pain']].map(([k, lbl]) => `<button data-knee="${k}" class="flex-1 rounded-full min-h-[44px] text-[15px] font-semibold ${state.knee === k ? (k === 'pain' ? 'bg-danger-tint text-danger' : k === 'niggle' ? 'bg-warn-tint text-warn' : 'bg-success-tint text-success') : 'bg-fill text-ink'}">${lbl}</button>`).join('')}
        </div>

        <button id="save-run" ${c ? '' : 'disabled'} class="grit-btn mt-5">Save run</button>
        <button data-cancel class="w-full text-inksoft font-bold py-3 mt-1">Cancel</button>
      </div>`;

    overlay.querySelectorAll('[data-type]').forEach((b) => b.onclick = () => { state.type = b.dataset.type; paint(); });
    overlay.querySelectorAll('[data-rpe]').forEach((b) => b.onclick = () => { state.rpe = Number(b.dataset.rpe); paint(); });
    overlay.querySelectorAll('[data-knee]').forEach((b) => b.onclick = () => { state.knee = b.dataset.knee; paint(); });
    overlay.querySelectorAll('input[data-f]').forEach((inp) => inp.oninput = () => { state[inp.dataset.f] = inp.value; const cc = calc(); overlay.querySelector('.grit-card.bg-pagebg span:last-child').textContent = paceText(cc); const sv = overlay.querySelector('#save-run'); if (sv) sv.disabled = !cc; });
    overlay.querySelector('[data-cancel]').onclick = close;
    const del = overlay.querySelector('#del-run');
    if (del) del.onclick = async () => {
      if (!(await confirmBox({ title: 'Delete this run?', confirmLabel: 'Delete', danger: true }))) return;
      const { error } = await deleteRun(existing.id) || {};
      if (error) { toast("Couldn't delete, try again", { tone: 'bad' }); return; }
      close(); toast('Run deleted'); onSaved?.(null);
    };
    overlay.querySelector('#save-run').onclick = async () => {
      const cc = calc(); if (!cc) return;
      const payload = cc.minutesOnly
        ? { type: state.type, date: state.date, actual_minutes: Math.round(cc.totalSec / 60 * 10) / 10, rpe: state.rpe, knee_feel: state.knee, ...(existing ? { actual_distance_mi: null, avg_pace: null, treadmill_mph: null } : {}) }
        : { type: state.type, date: state.date, actual_distance_mi: cc.dist, actual_minutes: Math.round(cc.totalSec / 60 * 10) / 10, treadmill_mph: cc.mph, avg_pace: cc.pace, rpe: state.rpe, knee_feel: state.knee };
      if (planContext) payload.planned = { week: planContext.week ?? null, slot: planContext.slot ?? null, plannedDistanceMi: planContext.plannedDistanceMi ?? null };
      const ok = existing ? await updateRun(existing.id, payload) : await saveRun(payload);
      if (!ok) { toast("Run didn't save, check your signal and try again", { tone: 'bad' }); return; }
      close();
      toast(ok === 'pending' ? 'Saved on your phone. It sends when you have signal.' : existing ? 'Run updated' : 'Run saved', { tone: ok === 'pending' ? 'info' : 'good' });
      onSaved?.({ ...payload, paceSec: cc.paceSec ?? null });
    };
  }

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  paint();
}

function close() { if (overlay) { overlay.remove(); overlay = null; } }
